<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { computed, ref, onMounted, useTemplateRef, getCurrentInstance } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { sortStreams } from '@/utils'
import Spinner from "@/components/ui/Spinner.vue"
import DynamicService from '@/services/DynamicService'
import AddDynamicPlayerModal from "@/components/modals/AddDynamicPlayerModal.vue"
import DemoVideoModal from '@/components/modals/DemoVideoModal.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import DynamicPlayerCard from '@/components/DynamicPlayer/DynamicPlayerCard.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconPlay from '@/components/icon/IconPlay.vue'
import IconPlayer from '@/components/icon/IconPlayer.vue'
import IconBookOpen from '@/components/icon/IconBookOpen.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'
import IconChevronUpDown from '@/components/icon/IconChevronUpDown.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import PageHeader from '@/components/ui/PageHeader.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import ListingCreateTile from '@/components/ui/ListingCreateTile.vue'
import ListingSearchInput from '@/components/ui/ListingSearchInput.vue'
import ViewModeSwitcher from '@/components/ui/ViewModeSwitcher.vue'
import ResponsiveToolbar from '@/components/ui/ResponsiveToolbar.vue'
import { planLimitReachedMessage } from '@/Utils/planLimitCopy'
import { usePersistedSort, DATE_NAME_SORT } from '@/Utils/usePersistedSort'

const { notify } = useNotification()
const store = useStore()
const router = useRouter()
const instance = getCurrentInstance()
const root = instance.proxy.$root
const addPlayerModalRef = useTemplateRef('addPlayerModalRef')

// Refs
const { sortBy, setSort } = usePersistedSort('castr.sort.dynamicPlayer', DATE_NAME_SORT, 'Newest')
const sortByDropdown = ref(null)
const sortByOptions = DATE_NAME_SORT.map((value) => ({ label: value, value }))
const searchQuery = ref('')
const playerToDelete = ref(null)

// Computed
const loadingStatus = computed(() => store.getters['Ui/loadingStatus'])
const dynamicPlayerFeatures = computed(() => store.getters['User/dynamicPlayerFeatures'])
const hasLiveSubscription = computed(() => store.getters['User/hasLiveSubscription'])
const hasVodSubscription = computed(() => store.getters['User/hasVodSubscription'])
const players = computed(() => store.getters['DynamicPlayers/dynamicPlayers'])
const showListControls = computed(() => (players.value?.length ?? 0) > 2)

// Plan allocation for dynamic players (dp:count). Only a positive number is a
// real cap; otherwise we just show the count without an "of Y" limit.
const playerLimit = computed(() => {
  const c = dynamicPlayerFeatures.value?.count
  return typeof c === 'number' && c > 0 ? c : null
})

const hasSubscription = computed(() => {
  return dynamicPlayerFeatures.value && (
    (hasLiveSubscription.value && hasLiveSubscription.value.enabled) ||
    (hasVodSubscription.value && hasVodSubscription.value.enabled)
  )
})

const playersSorted = computed(() => {
  if (!players.value) return []
  const withCreationTime = players.value.map((player) => ({
    ...player,
    creationTime: player.creationTime || player.createdAt || player._id,
  }))
  return sortStreams(withCreationTime, sortBy.value) || withCreationTime
})

const playersFiltered = computed(() => {
  const searchStr = searchQuery.value.toLowerCase()
  return playersSorted.value.filter(p => p?.name?.toLowerCase().includes(searchStr))
})

const dynamicHeaderUsage = computed(() => {
  if (!players.value?.length) return null
  const meta = {
    used: players.value.length,
    label: 'players',
    labelSingular: 'player',
  }
  if (playerLimit.value) {
    meta.limit = playerLimit.value
  }
  return meta
})

const atPlayerLimit = computed(() => {
  const max = playerLimit.value
  if (!max) return false
  return (players.value?.length ?? 0) >= max
})

const playerCreateDisabledTitle = computed(() => {
  if (atPlayerLimit.value) return planLimitReachedMessage('Dynamic player')
  return ''
})

const playersLoaded = computed(() => store.getters['DynamicPlayers/isLoaded'])
const shouldShowCompactModeStreams = computed(() => store.getters['Ui/shouldShowCompactModeStreams'])

// Hooks
onMounted(async () => {
  if (!playersLoaded.value) {
    store.dispatch('Ui/setLoadingStatus', true)
  } else {
    store.dispatch('Ui/setLoadingStatus', false)
  }
  try {
    await store.dispatch('DynamicPlayers/getPlayers')
  } finally {
    store.dispatch('Ui/setLoadingStatus', false)
  }
})

// Methods
const onNewPlayer = async () => {
  await store.dispatch('DynamicPlayers/getPlayers', true)
  router.push({ path: '/dynamic/' + store.state.DynamicPlayers.players.at(-1)._id })
}

const showAddDynamicPlayerModal = () => addPlayerModalRef.value?.toggleModal()

const togglePlayer = async (player) => {
  await DynamicService.togglePlayer(player._id)
  await store.dispatch('DynamicPlayers/getPlayers', true)
  notify({ group: 'success', text: `Dynamic Player "${player.name}" status changed` })
}

const showDeletePlayerModal = (player) => {
  playerToDelete.value = player
  showModal('delete-dynamic-player')
}

const removePlayer = async () => {
  if (!playerToDelete.value) return
  const playerName = playerToDelete.value.name
  try {
    await DynamicService.deletePlayer(playerToDelete.value._id)
    await store.dispatch('DynamicPlayers/getPlayers', true)
    notify({ group: 'success', text: `Dynamic Player "${playerName}" is completely deleted from your account` })
  } catch (err) {
    notify({ group: 'error', text: err.message || "Couldn't delete dynamic player" })
  }
}

const sortPlayers = (option) => {
  setSort(option)
  sortByDropdown.value?.toggleDropdown()
}

</script>

<template>
  <div class="container detail-shell page-title-area px-4">
    <PageHeader
      title="Dynamic Player"
      subtitle="Embed once, then swap the live stream or video behind your player anytime."
      mobile-inline
      :usage="dynamicHeaderUsage"
    >
      <Tooltip :active="atPlayerLimit" wrap-trigger :html="true" :hide="1000">
        <FormButton v-if="hasSubscription" class="gap-1" size="md" :disabled="atPlayerLimit" @click="showAddDynamicPlayerModal">
          <icon-base class="mr-2" fill="none">
            <icon-plus />
          </icon-base>
          <span>New</span><span class="hidden md:inline">&nbsp;dynamic</span><span>&nbsp;player</span>
        </FormButton>
        <template #content>
          <span class="text-surface-8 text-sm" v-html="playerCreateDisabledTitle" />
        </template>
      </Tooltip>
    </PageHeader>

    <div v-if="loadingStatus" class="flex items-center justify-center w-full min-h-[70vh]" aria-busy="true">
      <Spinner text="Loading your players…" classes="text-dark-8" spinner-color="var(--c-dark-8)" spinner-size="22px" />
    </div>

    <!-- No subscription: upgrade state -->
    <main v-else-if="!hasSubscription">
      <EmptyState
        title="Embed once, swap content anytime"
        subtitle="Embed the player on your site once, then switch the live stream or video behind it anytime without touching the embed code."
      >
        <template #icon>
          <icon-base width="28" height="28" fill="none">
            <icon-player />
          </icon-base>
        </template>
        <template #actions>
          <FormButton type="secondary" class="w-64 justify-center" target="_blank"
            href="https://docs.castr.com/en/articles/7785953-how-to-create-a-dynamic-player">
            <icon-base class="mr-2">
              <icon-book-open />
            </icon-base>
            How Dynamic Player works
          </FormButton>
          <FormButton type="secondary" class="w-64 justify-center" @click="showModal('modal-demo-video')">
            <icon-base class="mr-2">
              <icon-play />
            </icon-base>
            Watch a tutorial
          </FormButton>
          <FormButton v-if="hasLiveSubscription" class="w-64 justify-center" @click="showAddDynamicPlayerModal">
            <span>New</span><span class="hidden md:inline">&nbsp;dynamic</span><span>&nbsp;player</span>
          </FormButton>
          <FormButton v-else class="w-64 justify-center" to="/subscribe">
            Upgrade Plan
          </FormButton>
        </template>
      </EmptyState>
    </main>

    <!-- Subscribed but no players -->
    <main v-else-if="players?.length === 0">
      <EmptyState
        title="Embed once, swap content anytime"
        subtitle="Embed the player on your site once, then switch the live stream or video behind it anytime without touching the embed code."
      >
        <template #icon>
          <icon-base width="28" height="28" fill="none">
            <icon-player />
          </icon-base>
        </template>
        <template #actions>
          <FormButton type="secondary" class="w-64 justify-center" target="_blank"
            href="https://docs.castr.com/en/articles/7785953-how-to-create-a-dynamic-player">
            <icon-base class="mr-2">
              <icon-book-open />
            </icon-base>
            How Dynamic Player works
          </FormButton>
          <FormButton type="secondary" class="w-64 justify-center" @click="showModal('modal-demo-video')">
            <icon-base class="mr-2">
              <icon-play />
            </icon-base>
            Watch a tutorial
          </FormButton>
          <FormButton class="w-64 justify-center" @click="showAddDynamicPlayerModal">
            <span>New</span><span class="hidden md:inline">&nbsp;dynamic</span><span>&nbsp;player</span>
          </FormButton>
        </template>
      </EmptyState>
    </main>

    <!-- Subscribed with players: grid view -->
    <main v-else>
      <ResponsiveToolbar class="mt-6 mb-3">
        <template v-if="showListControls">
        <DropDownMenu type="secondary" ref="sortByDropdown" position="left">
          <template #toggle-button>
            <span class="text-surface-8">Sort by:&nbsp;</span>
            <span class="text-white">{{ sortBy }}</span>
            <icon-base class="!ml-1 shrink-0 text-surface-8">
              <icon-chevron-up-down />
            </icon-base>
          </template>
          <template #menu-items>
            <DropDownMenuItem v-for="option in sortByOptions" :key="option.value" :active="sortBy === option.value"
              @click="sortPlayers(option.value)">
              {{ option.label }}
              <icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === option.value" fill="none">
                <icon-checkmark />
              </icon-base>
            </DropDownMenuItem>
          </template>
        </DropDownMenu>
        <ListingSearchInput v-model="searchQuery" />
        </template>
        <ViewModeSwitcher class="shrink-0" />
      </ResponsiveToolbar>
      <ul class="grid auto-rows-fr"
        :class="shouldShowCompactModeStreams ? '!grid-cols-1 gap-1.5' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'">
        <li v-for="player in playersFiltered" :key="player._id" class="h-full">
          <DynamicPlayerCard :player="player" :compact-mode="shouldShowCompactModeStreams"
            @toggle="togglePlayer(player)"
            @delete="showDeletePlayerModal(player)" />
        </li>
        <li v-if="!searchQuery" class="h-full">
          <ListingCreateTile
            label="Player"
            :view="shouldShowCompactModeStreams ? 'list' : 'thumbnail'"
            :used="players?.length ?? 0"
            :limit="playerLimit"
            :limit-reached="atPlayerLimit"
            @create="showAddDynamicPlayerModal"
          />
        </li>
      </ul>
    </main>

    <add-dynamic-player-modal ref="addPlayerModalRef" @player-added="onNewPlayer" />
    <demo-video-modal videoid="9UJ1aZUoaRqiJ86YfFYKG" />
    <confirm-modal modalId="delete-dynamic-player" message="Are you sure you want to delete this dynamic player?"
      modal-type="danger" @modal-confirm="removePlayer" />
  </div>
</template>

<style scoped></style>
